import React, { useEffect, useMemo, useState } from "react";
import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import {
	getPromoPortfolioProjectsAdmin,
	deletePromoPortfolioProject,
	movePromoPortfolioProject,
} from "../../../../api/promo-portfolio-project";
import {
	Button,
	Tooltip,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import { buttonsStyle } from "../../../components/tables/table";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { useHistory } from "react-router-dom";
import { SERVER_URL } from "../../../../utils";

function resolveMediaUrl(raw) {
	const p = String(raw || "").trim();
	if (!p) return "";
	if (/^https?:\/\//i.test(p) || p.startsWith("data:")) return p;
	if (p.startsWith("/")) return `${String(SERVER_URL || "").replace(/\/$/, "")}${p}`;
	return `${String(SERVER_URL || "").replace(/\/$/, "")}/${p.replace(/^\/+/, "")}`;
}

function sectorNames(project) {
	const list = project.sectorIds || [];
	return list.map((s) => s?.name || "").filter(Boolean).join(", ");
}

export default function PromoPortfolioProjectsPage() {
	const [projects, setProjects] = useState([]);
	const [projectId, setProjectId] = useState(null);
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const [refresh, setRefresh] = useState(false);
	const [movingId, setMovingId] = useState(null);
	const history = useHistory();

	const sorted = useMemo(
		() =>
			[...(projects || [])].sort(
				(a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || String(a.key).localeCompare(String(b.key))
			),
		[projects]
	);

	useEffect(() => {
		let mounted = true;
		getPromoPortfolioProjectsAdmin()
			.then((res) => {
				if (!mounted) return;
				if (res.status === 200) setProjects(res.data || []);
			})
			.catch((error) => {
				if (!mounted) return;
				alertError({ error, customMessage: "No se pudieron cargar los proyectos." });
			})
			.finally(() => {
				if (mounted) setRefresh(false);
			});
		return () => {
			mounted = false;
		};
	}, [refresh]);

	const handleMove = (id, direction) => {
		setMovingId(id);
		movePromoPortfolioProject(id, direction)
			.then((res) => {
				if (res.status === 200) setRefresh(true);
			})
			.catch((error) => {
				alertError({ error, customMessage: "No se pudo cambiar el orden." });
			})
			.finally(() => setMovingId(null));
	};

	return (
		<>
			<Card>
				<CardHeader title="Portfolio web (evenely.com)">
					<CardHeaderToolbar>
						<Button
							variant="outlined"
							color="default"
							style={{ marginRight: 8 }}
							onClick={() => history.push("/promo-portfolio-sectors")}
						>
							Gestionar sectores
						</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={() => history.push("/edit-promo-portfolio-project")}
						>
							Nuevo proyecto
						</Button>
					</CardHeaderToolbar>
				</CardHeader>
				<CardBody>
					{sorted.length === 0 ? (
						<Typography color="textSecondary">No hay proyectos. Crea el primero con «Nuevo proyecto».</Typography>
					) : (
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell width={100}>Portada</TableCell>
										<TableCell>Proyecto</TableCell>
										<TableCell>Cliente</TableCell>
										<TableCell>Sectores</TableCell>
										<TableCell width={90}>Destacado</TableCell>
										<TableCell width={90}>Estado</TableCell>
										<TableCell width={200} align="right">
											Acciones
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{sorted.map((project, index) => {
										const id = project._id;
										const cover =
											(project.media || []).find((m) => m.type === "image")?.url ||
											project.media?.[0]?.url ||
											"";
										const src = resolveMediaUrl(cover);
										const isFirst = index === 0;
										const isLast = index === sorted.length - 1;
										const active = project.active !== false;
										const title = project.locales?.es?.title || project.key;
										return (
											<TableRow key={id}>
												<TableCell>
													{src ? (
														<img
															src={src}
															alt=""
															style={{ maxHeight: 48, maxWidth: 80, objectFit: "cover", borderRadius: 4 }}
															onError={(e) => {
																e.currentTarget.style.display = "none";
															}}
														/>
													) : (
														"—"
													)}
												</TableCell>
												<TableCell>
													<strong>{title}</strong>
													<div style={{ fontSize: 12, color: "#888" }}>{project.key}</div>
												</TableCell>
												<TableCell>{project.client || "—"}</TableCell>
												<TableCell>{sectorNames(project) || "—"}</TableCell>
												<TableCell>
													{project.featured ? (
														<Chip label="Sí" size="small" color="primary" />
													) : (
														<Chip label="No" size="small" variant="outlined" />
													)}
												</TableCell>
												<TableCell>
													{active ? (
														<Chip label="Activo" size="small" style={{ background: "#1bc5bd", color: "#fff" }} />
													) : (
														<Chip label="Inactivo" size="small" />
													)}
												</TableCell>
												<TableCell align="right">
													<Tooltip title="Editar">
														<Button
															size="small"
															style={buttonsStyle}
															onClick={() => history.push("/edit-promo-portfolio-project/" + id)}
														>
															<EditIcon />
														</Button>
													</Tooltip>
													<Tooltip title="Eliminar">
														<Button
															size="small"
															style={buttonsStyle}
															onClick={() => {
																setProjectId(id);
																setOpenConfirmDialog(true);
															}}
														>
															<DeleteIcon />
														</Button>
													</Tooltip>
													<Tooltip title="Subir">
														<span>
															<Button
																size="small"
																style={buttonsStyle}
																disabled={isFirst || movingId === id}
																onClick={() => handleMove(id, "up")}
															>
																<ArrowUpwardIcon fontSize="small" />
															</Button>
														</span>
													</Tooltip>
													<Tooltip title="Bajar">
														<span>
															<Button
																size="small"
																style={buttonsStyle}
																disabled={isLast || movingId === id}
																onClick={() => handleMove(id, "down")}
															>
																<ArrowDownwardIcon fontSize="small" />
															</Button>
														</span>
													</Tooltip>
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					)}
					<ConfirmDialog
						title="¿Seguro que quieres eliminar este proyecto?"
						open={openConfirmDialog}
						setOpen={setOpenConfirmDialog}
						onConfirm={() => {
							deletePromoPortfolioProject(projectId)
								.then((res) => {
									if (res.status === 204) {
										alertSuccess({ title: "Eliminado", customMessage: "Proyecto eliminado correctamente." });
										setRefresh(true);
									}
								})
								.catch((error) => {
									alertError({ error, customMessage: "No se pudo eliminar el proyecto." });
								})
								.finally(() => {
									setOpenConfirmDialog(false);
									setProjectId(null);
								});
						}}
					/>
				</CardBody>
			</Card>
		</>
	);
}
